import React from "react";
import PropTypes from "prop-types";
import { Box, Chip, Grid } from "@mui/material";
import Typography from "@limitless/modern/data-display/typography/Typography";
import ArrowRight from "@limitless/icons/ArrowRight";
import { useExpertProfileContext, useExpertOnboardingContext } from "@limitless/expert";
import { useNavigation } from "@limitless/portal";
import { recordGroupName } from "@limitless/analytics";
import { useTranslation } from "@limitless/common/hooks";

import makeStyles from "@mui/styles/makeStyles";
import {
    extraInfoDetails,
    getMemberDetailsStatus,
    getPracticeQuestionStatus,
    isDocumentVerificationRequired,
    isRedChip,
    shouldPracticeTaskVisible
} from "@limitless/expert/utils/OnboardingStatsHelper";

const useStyles = makeStyles(theme => {
    return {
        row2Text: {
            color: theme.palette.primary.main
        },
        labelBox: {
            display: "flex",
            alignItems: "center",
            marginRight: 10
        },
        icon: {
            marginLeft: theme.spacing(1),
            color: theme.palette.primary.main,
            fontSize: "14px !important"
        },
        chip: {
            backgroundColor: theme.palette.secondary.light,
            minWidth: "85px",
            maxWidth: "100%",
            borderRadius: "8px",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis"
        },

        chipRed: {
            backgroundColor: theme.palette.error.light,
            minWidth: "85px",
            maxWidth: "100%",
            borderRadius: "8px",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis"
        }
    };
});

const OnboardingProgressCard = ({ onboardingDetail = {} }) => {
    const { profile } = useExpertProfileContext();
    const { getOnboardingDetail } = useExpertOnboardingContext();
    const { navigate } = useNavigation();
    const { t } = useTranslation();

    const classes = useStyles();

    const isPayPalVerified = profile.paypalAccount;

    const items = [
        {
            label: "Practice Tasks",
            status: getPracticeQuestionStatus(onboardingDetail),
            visible: shouldPracticeTaskVisible(onboardingDetail),
            navigateTo: "/onboarding/detail?tab=practiceTasksTab"
        },
        {
            label: "Extra Details",
            visible: !!extraInfoDetails(onboardingDetail),
            status: getMemberDetailsStatus(onboardingDetail),
            navigateTo: "/onboarding/detail?tab=memberDetailsTab"
        },
        // Commented out as part of ticket LD-15156: Hide ID Verification / Document Verification
        // from all onboarding screens,summary, Detail tab, and Progress card.

        // {
        //     label: "ID Verification",
        //     visible: true,
        //     status: `${profile?.identityVerification?.status === "match" ? "completed" : "to do"}`,
        //     navigateTo: "/onboarding/detail?tab=idVerificationTab"
        // },
        // {
        //     label: "Document Verification",
        //     visible: isDocumentVerificationRequired(onboardingDetail, profile.documentVerification?.result),
        //     status: `${
        //         isDocumentVerificationRequired(onboardingDetail, profile.documentVerification?.result)
        //             ? "to do"
        //             : "completed"
        //     }`,
        //     navigateTo: "/onboarding/detail?tab=documentVerificationTab"
        // },
        {
            label: "Payment Details",
            visible: true,
            status: `${!isPayPalVerified ? "to do" : "complete"}`,
            navigateTo: "/settings?tab=payment"
        }
    ];

    return (
        <Box
            sx={{
                p: 2,
                mt: 1,
                boxShadow: "0px 2px 4px 0px rgba(33, 36, 41, 0.05)",
                borderRadius: "12px",
                backgroundColor: theme => theme.palette.background.paper
            }}
        >
            <Typography variant="h6" fontSize={18} fontWeight={500}>
                {"Your Progress"}
            </Typography>

            <Typography variant="subtitle2" mt={2} mb={2} className={classes.labelBox}>
                {items.filter(item => item.visible).every(item => item?.status === "completed")
                    ? "Everything complete, well done!"
                    : "Here’s what you need to do:"}
            </Typography>

            <Grid item xs={6} md={6} lg={12}>
                {items.map(
                    (item, index) =>
                        item.visible && (
                            <Grid container key={index} mb={2} alignItems="center">
                                <Grid
                                    item
                                    xs={9}
                                    lg={10}
                                    style={{ cursor: item.navigateTo ? "pointer" : "default" }}
                                    onClick={() => {
                                        if (item.navigateTo) {
                                            getOnboardingDetail(onboardingDetail);
                                            navigate(item.navigateTo);
                                            recordGroupName(onboardingDetail?.groupName);
                                        }
                                    }}
                                >
                                    <Typography variant="body2" className={classes.row2Text}>
                                        {item.label}
                                        <ArrowRight className={classes.icon} />
                                    </Typography>
                                </Grid>
                                <Grid item xs={4} lg={2}>
                                    <Box display="flex" justifyContent="flex-end">
                                        <Chip
                                            className={isRedChip(item?.status) ? classes.chipRed : classes.chip}
                                            label={t(item?.status) || ""}
                                        />
                                    </Box>
                                </Grid>
                            </Grid>
                        )
                )}
            </Grid>
        </Box>
    );
};

OnboardingProgressCard.propTypes = {
    onboardingDetail: PropTypes.object
};

export default OnboardingProgressCard;
